import { useLocalStorage, useWindowSize } from '@vueuse/core'
import { computed, ref } from 'vue'
import type { Ref } from 'vue'

export const RIGHTY_DOCK_DEFAULT_WIDTH = 400
export const RIGHTY_DOCK_MIN_WIDTH = 200
export const RIGHTY_DOCK_MAX_WIDTH = 720
const KEY_STEP = 16
const KEY_STEP_LARGE = 64

interface Drag {
  pointerId: number
  startX: number
  startWidth: number
}

/**
 * The desktop dock's content width: dragged or keyed from its left edge, clamped to
 * 200px..min(720px, half the viewport), and saved per browser. `onResized` runs once
 * per finished resize; animated keyboard steps leave that to the dock's width
 * `transitionend`.
 */
export function useRightyDockWidth(options: { animated: Ref<boolean>; onResized: () => void }) {
  const stored = useLocalStorage('rm_righty_dock_width', RIGHTY_DOCK_DEFAULT_WIDTH)
  const { width: viewportWidth } = useWindowSize()
  const max = computed(() =>
    Math.max(RIGHTY_DOCK_MIN_WIDTH, Math.min(RIGHTY_DOCK_MAX_WIDTH, Math.floor(viewportWidth.value / 2)))
  )
  const clamp = (value: number) =>
    Math.round(
      Math.min(max.value, Math.max(RIGHTY_DOCK_MIN_WIDTH, Number.isFinite(value) ? value : RIGHTY_DOCK_DEFAULT_WIDTH))
    )

  const drag = ref<Drag | null>(null)
  const draft = ref<number | null>(null)
  const width = computed(() => clamp(draft.value ?? Number(stored.value)))
  const resizing = computed(() => drag.value !== null)
  let keyed = false

  function onPointerdown(event: PointerEvent) {
    if (event.button !== 0 || drag.value) {
      return
    }
    event.preventDefault()
    ;(event.currentTarget as HTMLElement).setPointerCapture?.(event.pointerId)
    drag.value = { pointerId: event.pointerId, startX: event.clientX, startWidth: width.value }
    draft.value = width.value
  }

  function onPointermove(event: PointerEvent) {
    if (drag.value?.pointerId === event.pointerId) {
      draft.value = clamp(drag.value.startWidth + drag.value.startX - event.clientX)
    }
  }

  function endResize() {
    if (!drag.value) {
      return
    }
    const { startWidth } = drag.value
    const next = draft.value ?? startWidth
    drag.value = null
    draft.value = null
    if (next !== startWidth) {
      stored.value = next
      options.onResized()
    }
  }

  function onPointerend(event: PointerEvent) {
    if (drag.value?.pointerId === event.pointerId) {
      endResize()
    }
  }

  function onKeydown(event: KeyboardEvent) {
    const step = event.shiftKey ? KEY_STEP_LARGE : KEY_STEP
    const target = {
      ArrowLeft: width.value + step,
      ArrowRight: width.value - step,
      Home: RIGHTY_DOCK_MIN_WIDTH,
      End: max.value
    }[event.key]
    if (target === undefined || drag.value) {
      return
    }
    // The flow canvas pans on arrow keys at the window; a handled resize key must not also pan it.
    event.preventDefault()
    event.stopPropagation()
    const next = clamp(target)
    if (next !== width.value) {
      stored.value = next
      keyed = true
    }
  }

  function onKeyup() {
    if (keyed && !options.animated.value) {
      options.onResized()
    }
    keyed = false
  }

  return {
    width,
    min: RIGHTY_DOCK_MIN_WIDTH,
    max,
    resizing,
    onPointerdown,
    onPointermove,
    endResize,
    onPointerend,
    onKeydown,
    onKeyup
  }
}
